import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { formatDateTime, sectorLabel, statusMeta } from "@/lib/constants";
import { ArrowRight, PlusCircle } from "lucide-react";

const StatCard = ({ label, value, tone = "default", testId }) => {
  const tones = {
    default: "border-slate-200",
    olumlu: "border-orange-200",
    olumsuz: "border-red-200",
    islemde: "border-slate-300",
    anlasildi: "border-emerald-200",
    aranmali: "border-sky-200",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} bg-white p-6`} data-testid={testId}>
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">{label}</div>
      <div className="text-4xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
};

export default function Home() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, list] = await Promise.all([
        api.get("/calls/stats"),
        api.get("/calls", { params: { page: 1, page_size: 6 } }),
      ]);
      setStats(s.data);
      setRecent(list.data.items || []);
    })();
  }, []);

  return (
    <Layout>
      <div className="mb-10 flex justify-between items-end border-b border-slate-100 pb-6" data-testid="home-header">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900">Ana Sayfa</h1>
          <p className="text-sm text-slate-500 mt-2">Reklam çağrılarınıza hızlı bir bakış.</p>
        </div>
        <Link
          to="/calls/new"
          data-testid="home-new-btn"
          className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-5 py-2.5 font-medium text-sm transition-colors duration-150 flex items-center gap-2"
        >
          <PlusCircle size={16} /> Yeni Çağrı Ekle
        </Link>
      </div>

      <div className="grid grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
        <StatCard label="Toplam Kayıt" value={stats?.total ?? "—"} testId="stat-total" />
        <StatCard label="Aranmalı" value={stats?.aranmali ?? "—"} tone="aranmali" testId="stat-aranmali" />
        <StatCard label="Olumlu" value={stats?.olumlu ?? "—"} tone="olumlu" testId="stat-olumlu" />
        <StatCard label="Anlaşıldı" value={stats?.anlasildi ?? "—"} tone="anlasildi" testId="stat-anlasildi" />
      </div>

      <div className="flex items-end justify-between mb-4">
        <h2 className="text-lg tracking-tight font-semibold text-slate-900">Son Kayıtlar</h2>
        <Link to="/calls" data-testid="see-all-calls" className="text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1">
          Tümünü Gör <ArrowRight size={14} />
        </Link>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="recent-table-wrap">
        {recent.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="recent-empty">
            Henüz kayıt yok.
            <div className="mt-3">
              <Link to="/calls/new" className="text-orange-600 hover:text-orange-700 font-medium">
                İlk çağrınızı ekleyin →
              </Link>
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Marka</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Yetkili</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sektör</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Güncelleme</th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Durum</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-none hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 text-sm font-medium text-slate-900">
                    <Link to={`/calls/${c.id}`} data-testid={`recent-row-${c.id}`}>{c.brand_name}</Link>
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-700">{c.contact_name}</td>
                  <td className="py-4 px-6 text-sm text-slate-700">{sectorLabel(c.sector, c.sector_other)}</td>
                  <td className="py-4 px-6 text-sm text-slate-500">{formatDateTime(c.updated_at)}</td>
                  <td className="py-4 px-6"><StatusBadge status={c.current_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
